"use client"

import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  ScriptableContext,
  Title,
  Tooltip,
} from "chart.js"
import { GetStockDataResponse, StockRange, getStockData } from "lib/api"
import { StatsType } from "lib/utils"
import Link from "next/link"
import { useState } from "react"
import { Chart } from "react-chartjs-2"
import { LeftArrowShort, NewText, RightArrowShort, TitleWithLine } from "ui"
import { sharedColors } from "ui/theme/colors"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Filler,
  Legend,
)

interface GraphData {
  price: string
  date: string
  volume: number
}

// Define trend type
type Trend = "up" | "down" | "neutral"

// Define colors based on trend - matched from Figma design
const trendColors = {
  up: {
    border: "#00A300", // Brighter Green
    gradientStart: "rgba(0, 163, 0, 0.15)", // Green with lower opacity to match design
    gradientEnd: "rgba(255, 255, 255, 0)",
  },
  down: {
    border: "#E74848", // Brighter Red from Figma
    gradientStart: "rgba(231, 72, 72, 0.15)", // Red with lower opacity to match design
    gradientEnd: "rgba(255, 255, 255, 0)",
  },
  neutral: {
    border: "#808080", // Gray
    gradientStart: "rgba(128, 128, 128, 0.15)", // Gray with lower opacity
    gradientEnd: "rgba(255, 255, 255, 0)",
  },
}

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top" as const,
      display: false,
      labels: {
        pointStyleWidth: 0,
        boxWidth: 0,
        usePointStyle: true,
        pointStyle: "line",
      },
    },
    tooltip: {
      backgroundColor: "white",
      titleColor: "black",
      footerColor: "gray",
      bodyColor: "gray",
      callbacks: {
        title: (el) => {
          return `${el[0].formattedValue} ILA`
        },
        label: () => "",
        footer: (el) => {
          return `${el[0].label}`
        },
      },
    },
    title: {
      display: false,
      text: "Chart.js Line Chart",
    },
  },
  interaction: {
    mode: "index",
    intersect: false,
  },
  hover: {
    mode: "x",
    intersect: false,
  },
  elements: {
    point: {
      // radius: 0,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        maxTicksLimit: 11, // Show only 10 tick marks on the x-axis
      },
    },
    y: {
      ticks: {
        maxTicksLimit: 6,
      },
    }
  },
} as ChartOptions

const ChartContainer = styled.div`
  width: 333px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    width: 500px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 700px;
  }
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledContainer = styled.div`
  display: grid;
  gap: 28px;
  padding-top: 14px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr;
    gap: 60px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 80px;
  }
`
const StyledTabs = styled(Tabs)`
  display: flex;
`
const StyledTab = styled(Tab) <{ active: boolean; last: boolean }>`
  position: relative;
  min-width: 40px;
  min-height: 0;
  padding: 0;
  padding-bottom: 2px;
  padding-left: 8px;
  padding-right: 8px;
  font-size: 16px;
  border-right: ${(props) => (props.last ? "" : "1px solid gray")};
  ${(props) =>
    props.active &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 8px;
      right: 8px;
      height: 2px;
      background-color: ${sharedColors.blue}; // Color of the border-bottom
    }
    color:${sharedColors.blue} !important;
  `}
`
const CommContainer = styled.div`
  padding-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 45px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 65px;
  }
`

// Helper function to calculate trend from graph data
function calculateTrend(graphData: GraphData[]): Trend {
  if (!graphData || graphData.length < 2) {
    return "neutral";
  }

  // For trend calculation, compare first and last data points
  const first = parseFloat(graphData[0].price);
  const last = parseFloat(graphData[graphData.length - 1].price);
  const diff = last - first;

  if (diff > 0) {
    return "up";
  }
  if (diff < 0) {
    return "down";
  }
  return "neutral";
}

// This function now finds the baseline for the chart fill
function findBaseline(prices: number[]): number {
  if (prices.length === 0) { return 0; }
  // For chart fill, use a value well below the minimum to ensure complete coverage
  // This matches the Figma design where fill extends all the way to the bottom
  const min = Math.min(...prices);
  return min * 0.85; // 15% below the lowest point for complete fill
}

function formatDate(
  dateString: string,
  locale: string,
  state: StockRange,
): string {
  const dateLang = locale === "en" ? "en-US" : "he-IL"
  const date = new Date(dateString)
  let dateOptions: Intl.DateTimeFormatOptions
  if (state === "Days_1") {
    dateOptions = {
      hourCycle: "h24",
      hour: "2-digit",
      minute: "2-digit",
    }
  } else {
    dateOptions = {
      month: "short", // "January", "February", etc.
      day: "numeric", // 1, 2, ..., 31
      year: "2-digit", // 2021, 2022, ..., etc.
    }
  }

  const formattedDate = new Intl.DateTimeFormat(dateLang, dateOptions).format(
    date,
  )
  return formattedDate
}

function selectEquallyDistantData(
  graphData: GraphData[],
): GraphData[] {
  // Show all points if available
  return graphData || [];
}

const array = [
  { value: "Days_1", label: "1D", heLabel: "יום" },
  // { value: "Days_5", label: "5D", heLabel: "5ימ'" },
  { value: "Months_1", label: "1M", heLabel: `חודש` },
  { value: "Months_6", label: `6M`, heLabel: `חצי שנה` },
  // { value: "YearToDate", label: "YTD", heLabel: "YTD" },
  // { value: "Years_1", label: "1Y", heLabel: "שנה" },
  // { value: "Years_5", label: "5Y", heLabel: `5שנ'` },
  // { value: "Max", label: "Max", heLabel: "מקס'" },
]

const statsLabels = {
  open: { label: "Open", heLabel: "פתיחה" },
  high: { label: "High", heLabel: "גבוה" },
  low: { label: "Low", heLabel: "נמוך" },
  cap: { label: "Mkt cap", heLabel: "שווי שוק" },
  ratio: { label: "P/E ratio", heLabel: "מכפיל רווח" },
  yield: { label: "Div yield", heLabel: "תשואת דיבידנד" },
  wkhigh: { label: "52-wk high", heLabel: "שיא 52 שב'" },
  wklow: { label: "52-wk low", heLabel: "שפל 52 שב'" },
}

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 20px;
  row-gap: 10px;
`

const StyledStat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const BottomText = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const StyledLink = styled(Link)`
  display: flex;
  gap: 4px;
  align-items: center;
  text-decoration: none;
`
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}
function StockChart({
  stockData,
  locale,
  financialStats,
}: {
  stockData?: GetStockDataResponse
  locale: string
  financialStats?: StatsType
}) {
  const [state, setState] = useState(0)
  const stateTerm = array[state].value as StockRange
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setState(newValue)
  }
  const [lastData, setLastData] = useState<GetStockDataResponse | undefined>(stockData)

  const { data: queryData } = useQuery<GetStockDataResponse>({
    queryKey: ["stockData", stateTerm],
    queryFn: async () => {
      const data = await getStockData(stateTerm)
      setLastData(data)
      if (!data.Body?.graph && stateTerm === "Days_1") {
        setState(1)
      }
      return data
    },
    enabled: !!stateTerm,
    refetchOnWindowFocus: false,
    initialData: stateTerm === "Days_1" && stockData?.Body?.graph ? stockData : undefined,
  })

  // Use lastData as fallback if queryData is undefined (during loading)
  const displayData = queryData || lastData

  if (!displayData || !financialStats) {
    return null
  }

  if (!displayData?.Body?.graph) { return null }

  const graphData = displayData.Body?.graph?.flatMap((el) => el) || []

  // Calculate trend for the current time period
  const currentTrend = calculateTrend(graphData);

  // Get colors based on the trend
  const currentColors = trendColors[currentTrend];
  const selected = selectEquallyDistantData(graphData)
  const prices = selected.map((el) => parseFloat(el.price))
  const labels = selected.map((el) => formatDate(el.date, locale, stateTerm))
  const baseline = findBaseline(prices) // Using our renamed function

  const data = {
    labels,
    datasets: [
      {
        label: "Stock Price Over Time",
        data: prices,
        borderColor: currentColors.border,
        borderWidth: 2, // Match thickness from design example
        pointRadius: 0, // Hide points on line
        pointHoverRadius: 4, // Show points only on hover
        stepped: false, // Not stepped - continuous line
        // lineTension is deprecated, now we just use tension
        fill: {
          target: { value: baseline },
          above: (context: ScriptableContext<"line">) => {
            const ctx = context.chart.ctx
            const chartHeight = context.chart.height;
            // Use chart height for gradient calculation
            const gradient = ctx.createLinearGradient(0, 0, 0, chartHeight)
            gradient.addColorStop(0, currentColors.gradientStart)
            gradient.addColorStop(1, currentColors.gradientEnd)
            return gradient
          },
          below: "transparent",
        },
        tension: 0.1 // Make the line less smooth (straight lines)
      },
    ],
  } as ChartData

  return (
    <CommContainer>
      <TitleWithLine
        title={locale === "en" ? "Stock graph" : "גרף מניה"}
        titleColor="black"
      />
      <StyledContainer>
        <NewText
          component={"p"}
          largeDesktopFontSize={24}
          largeDesktopLineHeight="33.6px"
          desktopFontSize={16}
          desktopLineHeight={"22.4px"}
          mobileFontSize={18}
          mobileLineHeight={"24px"}
        >
          {locale === "en"
            ? "Paz Group share data, traded on the Tel Aviv Stock Exchange"
            : "נתוני מניית קבוצת פז, הנסחרת בבורסה לניירות ערך בתל אביב"}
        </NewText>
        <ChartContainer>
          <StyledTabs value={state} onChange={handleChange} role="tablist">
            {array.map((el, index) => (
              <StyledTab
                role="tab"
                aria-selected={index === state}
                key={index}
                active={index === state}
                last={index === array.length - 1}
                label={locale === "en" ? el.label : el.heLabel}
                {...a11yProps(index)}
              />
            ))}
          </StyledTabs>
          <div
            role="img"
            aria-label={
              locale === "en"
                ? `Stock price chart showing ${prices.length} data points. Current price: ${prices[prices.length - 1]?.toFixed(2)} ILS`
                : `גרף מחיר מניה עם ${prices.length} נקודות נתונים. מחיר נוכחי: ${prices[prices.length - 1]?.toFixed(2)} ש"ח`
            }
          >
            <Chart data={data} type="line" options={options} />
          </div>
          <StatsGrid>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.open[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.openPrice}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.cap[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.marketCap}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.wkhigh[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.yearHigh}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.high[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.maxPrice}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.ratio[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.peRatio}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.wklow[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.yearLow}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.low[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.minPrice}
              </NewText>
            </StyledStat>
            <StyledStat aria-label="Financial Statistics" role="figure">
              <NewText component={"span"} color="black" desktopFontSize={14}>
                {statsLabels.yield[locale === "en" ? "label" : "heLabel"]}
              </NewText>
              <NewText component={"span"} desktopFontSize={14} color="gray">
                {financialStats?.dividendYield}
              </NewText>
            </StyledStat>
          </StatsGrid>
          <BottomText>
            <NewText
              component={"p"}
              largeDesktopFontSize={20}
              largeDesktopLineHeight="33.6px"
              desktopFontSize={12}
              desktopLineHeight={"22.4px"}
              mobileFontSize={14}
              mobileLineHeight={"24px"}
            >
              {locale === "en" ? "Source" : "מקור"}:
            </NewText>
            <StyledLink
              href={
                "https://www.google.com/finance/quote/PAZ:TLV"
              }
              target="_blank"
            >
              <NewText
                component={"p"}
                largeDesktopFontSize={20}
                largeDesktopLineHeight="33.6px"
                desktopFontSize={12}
                desktopLineHeight={"22.4px"}
                mobileFontSize={14}
                color={sharedColors.darkBlue}
                mobileLineHeight={"24px"}
              >
                Google Finance
              </NewText>
              {locale === "en" ? (
                <RightArrowShort color={sharedColors.darkBlue} />
              ) : (
                <LeftArrowShort color={sharedColors.darkBlue} />
              )}
            </StyledLink>
          </BottomText>
        </ChartContainer>
      </StyledContainer>
    </CommContainer>
  )
}

export default StockChart
